import { DEFAULT_DELAY_MS, DEFAULT_MAX_RETRIES } from './constants'
import { logError } from './errorLogger'
import { sleep } from './helpers'

export interface AsyncScanQueueOptions<T> {
  maxRetries?: number
  delayMs?: number
  onSuccess?: (item: T) => void
  onError?: (item: T, error: unknown, retryCount: number) => void
  onComplete?: () => void
}

export class AsyncScanQueue<T> {
  private queue: T[] = []
  private processing = false
  private destroyed = false
  private handler: (item: T) => Promise<void>
  private options: Required<AsyncScanQueueOptions<T>>

  constructor(handler: (item: T) => Promise<void>, options: AsyncScanQueueOptions<T> = {}) {
    this.handler = handler
    this.options = {
      maxRetries: options.maxRetries ?? DEFAULT_MAX_RETRIES,
      delayMs: options.delayMs ?? DEFAULT_DELAY_MS,
      onSuccess: options.onSuccess ?? (() => null),
      onError: options.onError ?? (() => null),
      onComplete: options.onComplete ?? (() => null),
    }
  }

  enqueue(item: T): void {
    if (this.destroyed) return
    this.queue.push(item)
    this.processNext()
  }

  private async processNext(): Promise<void> {
    if (this.processing || this.queue.length === 0 || this.destroyed) return

    this.processing = true

    while (this.queue.length > 0 && !this.destroyed) {
      const item = this.queue.shift()!
      await this.processItem(item)
    }

    this.processing = false

    if (this.queue.length === 0 && !this.destroyed) {
      this.options.onComplete()
    }
  }

  private async processItem(item: T): Promise<void> {
    let lastError: unknown = null

    for (let attempt = 0; attempt <= this.options.maxRetries; attempt++) {
      try {
        await this.handler(item)
        this.options.onSuccess(item)
        return
      } catch (error) {
        lastError = error
        console.error(`[AsyncScanQueue] Attempt ${attempt + 1}/${this.options.maxRetries + 1} failed:`, error)
        logError(
          `Error processing item: ${JSON.stringify(item)} - Attempt ${attempt + 1} - Error: ${
            error instanceof Error ? error.message : String(error)
          }`
        )
        if (attempt < this.options.maxRetries && !this.destroyed) {
          await sleep(this.options.delayMs)
        }
      }
    }

    this.options.onError(item, lastError, this.options.maxRetries + 1)
  }

  destroy(): void {
    this.destroyed = true
    this.queue = []
  }
}
